import React, { Suspense, lazy } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from '@/contexts/AuthContext';
import { OrganizationProvider } from '@/contexts/OrganizationContext';
import { AdminProvider } from '@/contexts/AdminContext';
import { LanguageProvider } from '@/contexts/language';
import { RoleProvider } from '@/contexts/RoleContext';
import { CalendarProvider } from '@/contexts/CalendarContext';
// MockDataProvider removed - Phase 3: Real Data Integration
import { useSecurityMonitoring } from '@/hooks/useSecurityMonitoring';
import '@/utils/diagnostics'; // Load diagnostic tools for production debugging
import { AuthGate } from '@/components/auth/AuthGate';
import { SessionMonitor } from '@/components/auth/SessionMonitor';
import { TierAccessGate } from '@/components/auth/TierAccessGate';
import { OnboardingGuard } from '@/components/auth/OnboardingGuard';
import { BetaWrapper } from '@/components/auth/BetaWrapper';
import { Toaster } from '@/components/ui/toaster';
import { Toaster as Sonner } from 'sonner';
import { CookieConsentBanner } from '@/components/legal/CookieConsentBanner';
import { Header } from '@/components/layout/Header';
import { Sidebar } from '@/components/Sidebar';
import { SkipLink } from '@/components/accessibility/SkipLink';
import { SubscriptionPausedBlocker } from '@/components/subscription/SubscriptionPausedBlocker';
import { PaymentFailedBlocker } from '@/components/subscription/PaymentFailedBlocker';
import { BillingBlockBanner } from '@/components/billing/BillingBlockBanner';
import { SubscriptionProvider, useSubscriptionAccessFromContext } from '@/contexts/SubscriptionContext';
import ProtectedRoute from '@/components/ProtectedRoute';
import { GlobalErrorBoundary, RouteErrorBoundary } from '@/utils/errors';
import PageLoadingSpinner from '@/components/ui/PageLoadingSpinner';
import DevRouteWrapper from '@/components/routing/DevRouteWrapper';
import AnalyticsRouteWrapper from '@/components/routing/AnalyticsRouteWrapper';
import { ThemeProvider } from '@/components/theme-provider';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Analytics } from '@vercel/analytics/react';
import { HelpWidget } from '@/components/help/HelpWidget';
import { initWebVitals } from '@/lib/webVitals';
import { BetaStatusBanner } from '@/components/beta/BetaStatusBanner';
import { EnvironmentBanner } from '@/components/environment/EnvironmentBanner';

import NestedRouteProvider from '@/components/routing/NestedRouteProvider';
import { SubsidyRedirect } from '@/components/routing/SubsidyRedirect';
import { ProfileRedirect } from '@/components/routing/ProfileRedirect';
import { useAdminSubdomainRedirect } from '@/components/routing/AdminSubdomainRedirect';
import { useAuth } from '@/contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { logger } from '@/utils/logger';
import { shouldShowHeader, shouldShowAuthButtons, shouldShowSidebar } from '@/utils/headerVisibility';
import { TIMING } from '@/config/constants';

// 🚀 CONSOLIDATED: Lazy load unified pages
const Index = lazy(() => import('@/pages/Index'));
const IndexV2 = lazy(() => import('@/pages/IndexV2'));
const LandingV2 = lazy(() => import('@/pages/LandingV2'));
const NotreHistoirePage = lazy(() => import('@/pages/NotreHistoirePage'));
const Fonctionnalites = lazy(() => import('@/pages/Fonctionnalites'));
const Documentation = lazy(() => import('@/pages/Documentation'));
const DemarrageRapide = lazy(() => import('@/pages/DemarrageRapide'));
const GuidesUtilisateur = lazy(() => import('@/pages/GuidesUtilisateur'));
const Entreprise = lazy(() => import('@/pages/Entreprise'));
const Ressources = lazy(() => import('@/pages/Ressources'));
const AuthPage = lazy(() => import('@/pages/AuthPage'));
const AuthCallback = lazy(() => import('@/pages/AuthCallback'));
const EmailConfirmHandler = lazy(() => import('@/pages/EmailConfirmHandler'));
const EmailConfirmationPage = lazy(() => import('@/pages/EmailConfirmationPage').then(m => ({ default: m.EmailConfirmationPage })));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const AcceptInvitePage = lazy(() => import('@/pages/AcceptInvitePage'));
const PendingApprovalPage = lazy(() => import('@/pages/PendingApprovalPage'));
// DEPRECATED: Old role selection flow - no longer used
// const RoleSelectionScreen = lazy(() => import('@/pages/auth/RoleSelectionScreen').then(m => ({ default: m.RoleSelectionScreen })));
const MFAEnrollmentPage = lazy(() => import('@/pages/auth/MFAEnrollmentPage'));
const MFAChallengePage = lazy(() => import('@/pages/auth/MFAChallengePage'));
const UnifiedDashboard = lazy(() => import('@/pages/UnifiedDashboard'));
const UnifiedAdmin = lazy(() => import('@/pages/UnifiedAdmin'));

// PERF FIX: Store import promises for preloading critical bundles
// UnifiedProfile is the most common authenticated route, start loading immediately
const unifiedProfileImport = () => import('@/pages/UnifiedProfileFixed');
const workspaceImport = () => import('@/components/profile/ProfileAwareSubsidiesWorkspaceV2');
const UnifiedProfile = lazy(unifiedProfileImport);

// PERF FIX: Preload both profile page AND workspace component immediately
// This starts the bundle downloads BEFORE React even reaches the route
// Significantly reduces the 2.5s gap from nested lazy loading
const preloadStartTime = Date.now();
console.log(`📦 [PERF] Starting bundle preload at ${new Date().toISOString().split('T')[1]}`);
Promise.all([unifiedProfileImport(), workspaceImport()]).then(() => {
  console.log(`📦 [PERF] Bundles preloaded in ${Date.now() - preloadStartTime}ms`);
});

const UnifiedHelp = lazy(() => import('@/pages/UnifiedHelp'));

// Core functionality pages
const DecouvrirSubventionsPage = lazy(() => import('@/pages/DecouvrirSubventionsPage'));
const DecouvrirConsultantsPage = lazy(() => import('@/pages/DecouvrirConsultantsPage'));
const ProductDetailsPage = lazy(() => import('@/pages/ProductDetailsPage'));
const PricingPage = lazy(() => import('@/pages/PricingPage'));
const TarifsPage = lazy(() => import('@/pages/TarifsPage'));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage').then(m => ({ default: m.PaymentSuccessPage })));
const PaymentCancelPage = lazy(() => import('@/pages/PaymentCancelPage').then(m => ({ default: m.PaymentCancelPage })));
// DEPRECATED: Old account type selection flow - redirects to /auth now
// const AccountTypeSelection = lazy(() => import('@/components/auth/AccountTypeSelection').then(m => ({ default: m.AccountTypeSelection })));
// const ExpertRegistrationWizard = lazy(() => import('@/components/auth/expert/ExpertRegistrationWizard').then(m => ({ default: m.ExpertRegistrationWizard })));
// const ExpertRegistrationSuccess = lazy(() => import('@/components/auth/expert/ExpertRegistrationSuccess').then(m => ({ default: m.ExpertRegistrationSuccess })));
const NewProfilePage = lazy(() => import('@/pages/NewProfilePage'));
const ProfileEditPage = lazy(() => import('@/pages/ProfileEditPage'));
const SubsidySearchPage = lazy(() => import('@/pages/SubsidySearchPage'));
const SubsidyDetailPage = lazy(() => import('@/pages/SubsidyDetailPage'));
const ApplicationFormPage = lazy(() => import('@/pages/ApplicationFormPage'));
const CalendarPage = lazy(() => import('@/pages/CalendarPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const BulkImportPage = lazy(() => import('@/pages/BulkImportPage'));
const MonitoringPage = lazy(() => import('@/pages/MonitoringPage'));
const URLHealthDashboardPage = lazy(() => import('@/pages/URLHealthDashboard'));
const AIAssistantPage = lazy(() => import('@/pages/AIAssistantPage'));
const OnboardingPage = lazy(() => import('@/pages/Onboarding'));
const QATestPage = lazy(() => import('@/pages/QATestPage'));
const SEODashboardPage = lazy(() => import('@/pages/SEODashboardPage'));
// AdminTierSwitcherPage removed - legacy admin archived to /admin-legacy
// ProfileAwareTestPage removed - Phase 3: Real Data Integration

// Preferences pages
const EmailPreferencesPage = lazy(() => import('@/pages/preferences/EmailPreferencesPage'));
const UnsubscribePage = lazy(() => import('@/pages/preferences/UnsubscribePage'));

// Organization pages (Enterprise tier)
const OrganizationSetup = lazy(() => import('@/components/organization/OrganizationSetup').then(m => ({ default: m.OrganizationSetup })));
const TeamDashboard = lazy(() => import('@/components/organization/TeamDashboard').then(m => ({ default: m.TeamDashboard })));

// Legal pages
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'));
const TermsPage = lazy(() => import('@/pages/TermsPage'));
const CookiesPage = lazy(() => import('@/pages/CookiesPage'));
const ContactPage = lazy(() => import('@/pages/ContactPage'));
const PrivacyDashboardPage = lazy(() => import('@/pages/PrivacyDashboardPage'));
const MentionsLegalesPage = lazy(() => import('@/pages/MentionsLegalesPage'));
const CGVPage = lazy(() => import('@/pages/CGVPage'));

// Regional landing pages
const IleDeFrancePage = lazy(() => import('@/pages/regions/IleDeFrancePage'));
const AuvergneRhoneAlpesPage = lazy(() => import('@/pages/regions/AuvergneRhoneAlpesPage'));
const NouvelleAquitainePage = lazy(() => import('@/pages/regions/NouvelleAquitainePage'));
const OccitaniePage = lazy(() => import('@/pages/regions/OccitaniePage'));
const PACAPage = lazy(() => import('@/pages/regions/PACAPage'));

// Educational guide pages (SEO)
const GuideSubventionsFrance2025 = lazy(() => import('@/pages/guides/GuideSubventionsFrance2025'));
const GuideBPIFrance = lazy(() => import('@/pages/guides/GuideBPIFrance'));
const GuideSubventionsVsPrets = lazy(() => import('@/pages/guides/GuideSubventionsVsPrets'));
const GuideMaPrimeRenov = lazy(() => import('@/pages/guides/GuideMaPrimeRenov'));
const GuideAgricultureBio = lazy(() => import('@/pages/guides/GuideAgricultureBio'));

// Blog pages
const BlogPage = lazy(() => import('@/pages/BlogPage'));
const BlogArticlePage = lazy(() => import('@/pages/BlogArticlePage'));

// Audience-specific landing pages
const EntreprisesPage = lazy(() => import('@/pages/EntreprisesPage'));
const ConsultantsPage = lazy(() => import('@/pages/ConsultantsPage'));
const ParticuliersPage = lazy(() => import('@/pages/ParticuliersPage'));

// Product landing pages
const SimulateursLandingPage = lazy(() => import('@/pages/SimulateursLandingPage').then(m => ({ default: m.SimulateursLandingPage })));
const PlateformeLandingPage = lazy(() => import('@/pages/PlateformeLandingPage').then(m => ({ default: m.PlateformeLandingPage })));

const NotFound = lazy(() => import('@/pages/NotFound'));
const HealthCheckPage = lazy(() => import('@/pages/HealthCheckPage'));

// Admin V2 - New lean admin dashboard (for admin.subvention360.com)
// New admin (based on masubventionpro model)
const AdminLayout = lazy(() => import('@/pages/admin-new/AdminLayout'));
const AdminRoute = lazy(() => import('@/pages/admin-new/AdminRoute'));
const AdminDashboardPage = lazy(() => import('@/pages/admin-new/AdminDashboardPage'));
const AdminUsersPage = lazy(() => import('@/pages/admin-new/AdminUsersPage'));
const AdminUserDetailPage = lazy(() => import('@/pages/admin-new/AdminUserDetailPage'));
const AdminSubsidiesPage = lazy(() => import('@/pages/admin-new/AdminSubsidiesPage'));
const AdminSubsidyEditPage = lazy(() => import('@/pages/admin-new/AdminSubsidyEditPage'));
const AdminBlogPage = lazy(() => import('@/pages/admin-new/AdminBlogPage'));
const AdminBlogEditPage = lazy(() => import('@/pages/admin-new/AdminBlogEditPage'));

// 🚀 PERFORMANCE: Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: TIMING.CACHE.STALE_TIME_MS,
      gcTime: TIMING.CACHE.GC_TIME_MS,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// 🚀 PERFORMANCE: Memoized components
const ProtectedRouteWrapper = React.memo(({ children }: { children: React.ReactNode }) => (
  <ProtectedRoute>{children}</ProtectedRoute>
));

const OnboardingGuardWrapper = React.memo(({ children }: { children: React.ReactNode }) => (
  <OnboardingGuard requireOnboardingComplete>{children}</OnboardingGuard>
));

const SuspenseWrapper = React.memo(({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoadingSpinner />}>
    {children}
  </Suspense>
));

const SecurityWrapper = React.memo(({ children }: { children: React.ReactNode }) => {
  useSecurityMonitoring();

  React.useEffect(() => {
    // Initialize Web Vitals tracking (once globally)
    initWebVitals();

    // Preload critical chunks
    import('@/utils/dynamicImports').then(({ preloadCriticalChunks }) => {
      preloadCriticalChunks();
    });
  }, []);

  return (
    <>
      <SessionMonitor />
      {children}
    </>
  );
});

// Redirect component that preserves query params (for Supabase email links)
const AuthResetPasswordRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/reset-password${location.search}${location.hash}`} replace />;
};

// Main layout wrapper that includes the header and sidebar conditionally
// ✅ REFACTORING (2025-11-08): Simplified complex header logic using utility function
// ✅ ENHANCEMENT (2025-11-12): Added sidebar for authenticated users
// ✅ SECURITY FIX (2025-12-17): Centralized sidebar visibility to prevent showing on auth/public pages
const LayoutWrapper = React.memo(({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();

  // Handle admin subdomain redirect
  useAdminSubdomainRedirect();
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(() => {
    return localStorage.getItem('sidebar-collapsed') === 'true';
  });

  // Listen for sidebar collapse events
  React.useEffect(() => {
    const handleSidebarCollapse = (e: CustomEvent) => {
      setSidebarCollapsed(e.detail.isCollapsed);
    };

    window.addEventListener('sidebar-collapsed' as any, handleSidebarCollapse);
    return () => {
      window.removeEventListener('sidebar-collapsed' as any, handleSidebarCollapse);
    };
  }, []);

  // Use centralized visibility utilities
  const layoutConfig = {
    pathname: location.pathname,
    search: location.search,
    isAuthenticated: !!user,
  };

  const showHeader = shouldShowHeader(layoutConfig);
  const showAuthButtons = shouldShowAuthButtons(location.pathname, !!user);

  // SECURITY: Use centralized function that covers ALL cases where sidebar should be hidden
  const showSidebar = shouldShowSidebar(layoutConfig);

  // Calculate margin based on sidebar state (only apply when sidebar is shown)
  const mainMargin = showSidebar ? (sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60') : '';

  return (
    <>
      {/* Environment banner for staging/preview deployments */}
      <EnvironmentBanner />
      {showHeader && <Header showAuthButtons={showAuthButtons} />}
      {showSidebar && <Sidebar />}
      <div className={`${showHeader ? 'mt-16' : ''} ${mainMargin}`} style={{ transition: 'margin-left 300ms' }}>
        {showSidebar && <BetaStatusBanner />}
        {children}
      </div>
    </>
  );
});

/**
 * Subscription Access Wrapper
 *
 * Shows full-screen blocker if:
 * 1. Volume billing unpaid invoice (IMMEDIATE BLOCK - no grace period)
 * 2. Subscription payment failed and grace period expired/expiring
 * 3. Subscription paused and access has expired
 */
const SubscriptionAccessWrapper = React.memo(({ children }: { children: React.ReactNode }) => {
  // PERF FIX: Use consolidated context instead of separate hook
  const {
    hasAccess,
    isPaused,
    currentPeriodEnd,
    pausedAt,
    paymentFailed,
    paymentFailedAt,
    gracePeriodDaysRemaining,
    gracePeriodExpired
  } = useSubscriptionAccessFromContext();

  return (
    <>
      {/* Priority 0: Volume billing blocker (IMMEDIATE - no grace period) */}
      {/* BillingBlockBanner self-manages visibility via useBillingAccess hook */}
      <BillingBlockBanner />

      {/* Priority 1: Subscription payment failure blocker */}
      {paymentFailed && paymentFailedAt && (gracePeriodExpired || gracePeriodDaysRemaining <= 3) && (
        <PaymentFailedBlocker
          paymentFailedAt={paymentFailedAt}
          gracePeriodDaysRemaining={gracePeriodDaysRemaining}
          gracePeriodExpired={gracePeriodExpired}
        />
      )}

      {/* Priority 2: Subscription pause blocker (only if no payment failure) */}
      {!paymentFailed && !hasAccess && isPaused && currentPeriodEnd && (
        <SubscriptionPausedBlocker
          currentPeriodEnd={currentPeriodEnd}
          pausedAt={pausedAt || undefined}
        />
      )}

      {children}
    </>
  );
});

function App() {
  logger.debug('App: Rendering CONSOLIDATED version with Organizations');

  return (
    <HelmetProvider>
      <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
        <LanguageProvider>
          <GlobalErrorBoundary autoResetOnRouteChange={true}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <SubscriptionProvider>
              <OrganizationProvider>
                <AuthGate>
                  <AdminProvider>
                    <RoleProvider>
                      <CalendarProvider>
                        <NestedRouteProvider>
                        <SecurityWrapper>
                        <BetaWrapper>
                        <SubscriptionAccessWrapper>
                        <div className="min-h-screen bg-background font-sans antialiased">
                        <SkipLink />
                        <LayoutWrapper>
                          <Toaster />
                          <Sonner position="top-center" expand={false} richColors closeButton />
                          {/* <HelpWidget /> */}
                          <SpeedInsights />
                          <Analytics />
                          <SuspenseWrapper>
                            <main id="main-content">
                            <Routes>
                             {/* PUBLIC ROUTES */}
                             <Route path="/" element={
                               <RouteErrorBoundary routeName="Home Page">
                                 <LandingV2 />
                               </RouteErrorBoundary>
                             } />

                             {/* HEALTH CHECK ENDPOINT (for monitoring services) */}
                             <Route path="/health" element={
                               <RouteErrorBoundary routeName="Health Check">
                                 <HealthCheckPage />
                               </RouteErrorBoundary>
                             } />

                             <Route path="/landing-v2" element={
                               <RouteErrorBoundary routeName="Landing Page V2">
                                 <IndexV2 />
                               </RouteErrorBoundary>
                             } />
                             <Route path="/landing-old" element={
                               <RouteErrorBoundary routeName="Landing Page Old">
                                 <Index />
                               </RouteErrorBoundary>
                             } />
                             <Route path="/fonctionnalites" element={
                               <RouteErrorBoundary routeName="Features Page">
                                 <AnalyticsRouteWrapper pageName="Features">
                                   <Fonctionnalites />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/notre-histoire" element={
                               <RouteErrorBoundary routeName="Notre Histoire">
                                 <AnalyticsRouteWrapper pageName="Notre Histoire">
                                   <NotreHistoirePage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/documentation" element={
                               <RouteErrorBoundary routeName="Documentation Page">
                                 <AnalyticsRouteWrapper pageName="Documentation">
                                   <Documentation />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/documentation/demarrage-rapide" element={
                               <RouteErrorBoundary routeName="Quick Start Guide">
                                 <AnalyticsRouteWrapper pageName="Quick Start">
                                   <DemarrageRapide />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/documentation/guides-utilisateur" element={
                               <RouteErrorBoundary routeName="User Guides">
                                 <AnalyticsRouteWrapper pageName="User Guides">
                                   <GuidesUtilisateur />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/entreprise" element={
                               <RouteErrorBoundary routeName="Company Page">
                                 <AnalyticsRouteWrapper pageName="Company">
                                   <Entreprise />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/ressources" element={
                               <RouteErrorBoundary routeName="Resources Page">
                                 <AnalyticsRouteWrapper pageName="Resources">
                                   <Ressources />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/product-details" element={
                               <RouteErrorBoundary routeName="Product Details">
                                 <AnalyticsRouteWrapper pageName="Product Details">
                                   <ProductDetailsPage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/pricing" element={
                               <RouteErrorBoundary routeName="Pricing">
                                 <AnalyticsRouteWrapper pageName="Pricing">
                                   <PricingPage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/tarifs" element={
                               <RouteErrorBoundary routeName="Tarifs">
                                 <AnalyticsRouteWrapper pageName="Tarifs">
                                   <TarifsPage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/success" element={
                               <RouteErrorBoundary routeName="Payment Success">
                                 <AnalyticsRouteWrapper pageName="Payment Success">
                                   <PaymentSuccessPage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             <Route path="/cancel" element={
                               <RouteErrorBoundary routeName="Payment Cancel">
                                 <AnalyticsRouteWrapper pageName="Payment Cancel">
                                   <PaymentCancelPage />
                                 </AnalyticsRouteWrapper>
                               </RouteErrorBoundary>
                             } />
                             {/* HIDDEN: Decouvrir pages - redirected to landing */}
                             <Route path="/decouvrir" element={<Navigate to="/" replace />} />
                             <Route path="/decouvrir-subventions" element={<Navigate to="/" replace />} />
                             <Route path="/decouvrir/consultants" element={<Navigate to="/" replace />} />
                             {/* AUTH ROUTES */}
                             {/* DEPRECATED: Old account type/expert registration routes - redirect to /auth */}
                             <Route path="/auth/select-type" element={<Navigate to="/auth" replace />} />
                             <Route path="/auth/role-selection" element={<Navigate to="/auth" replace />} />
                             <Route path="/auth/expert-registration" element={<Navigate to="/auth" replace />} />
                             <Route path="/auth/expert-success" element={<Navigate to="/auth" replace />} />
                             <Route path="/auth" element={
                               <RouteErrorBoundary routeName="Auth Page">
                                 <AuthPage />
                               </RouteErrorBoundary>
                             } />
                             <Route path="/auth/callback" element={
                               <RouteErrorBoundary routeName="Auth Callback">
                                 <AuthCallback />
                               </RouteErrorBoundary>
                             } />
                            <Route path="/auth/confirm" element={
                              <RouteErrorBoundary routeName="Email Confirmation">
                                <EmailConfirmHandler />
                              </RouteErrorBoundary>
                            } />
                             <Route path="/auth/email-confirmation" element={
                               <RouteErrorBoundary routeName="Email Confirmation Page">
                                 <EmailConfirmationPage />
                               </RouteErrorBoundary>
                             } />
                             {/* MFA ROUTES (SEC-011) */}
                             <Route path="/auth/mfa-enroll" element={
                               <RouteErrorBoundary routeName="MFA Enrollment">
                                 <MFAEnrollmentPage />
                               </RouteErrorBoundary>
                             } />
                             <Route path="/auth/mfa-challenge" element={
                               <RouteErrorBoundary routeName="MFA Challenge">
                                 <MFAChallengePage />
                               </RouteErrorBoundary>
                             } />
                             <Route path="/reset-password" element={
                               <RouteErrorBoundary routeName="Reset Password">
                                 <ResetPassword />
                               </RouteErrorBoundary>
                             } />
                             {/* Redirect from /auth/reset-password to /reset-password (Supabase email links - preserves query params) */}
                             <Route path="/auth/reset-password" element={<AuthResetPasswordRedirect />} />
                             <Route path="/accept-invite/:token" element={
                               <RouteErrorBoundary routeName="Accept Invite">
                                 <AcceptInvitePage />
                               </RouteErrorBoundary>
                             } />
                            <Route path="/onboarding" element={
                              <RouteErrorBoundary routeName="Onboarding">
                                <ProtectedRouteWrapper>
                                  <AnalyticsRouteWrapper pageName="Onboarding">
                                    <OnboardingPage />
                                  </AnalyticsRouteWrapper>
                                </ProtectedRouteWrapper>
                              </RouteErrorBoundary>
                            } />
                            <Route path="/en-attente-approbation" element={
                              <RouteErrorBoundary routeName="Pending Approval">
                                <ProtectedRouteWrapper>
                                  <PendingApprovalPage />
                                </ProtectedRouteWrapper>
                              </RouteErrorBoundary>
                            } />

                            {/* CORE PROTECTED ROUTES - FRENCH URLs */}
                            {/* 🇫🇷 PRIMARY: French URL */}
                            <Route path="/tableau-de-bord" element={
                              <RouteErrorBoundary routeName="Dashboard" fallbackPath="/">
                                <OnboardingGuardWrapper>
                                  <TierAccessGate requireFullAccess>
                                    <AnalyticsRouteWrapper pageName="Dashboard">
                                      <UnifiedDashboard />
                                    </AnalyticsRouteWrapper>
                                  </TierAccessGate>
                                </OnboardingGuardWrapper>
                              </RouteErrorBoundary>
                            } />
                            {/* 🔄 REDIRECT: English → French (preserve external links) */}
                            <Route path="/dashboard" element={<Navigate to="/tableau-de-bord" replace />} />

                            {/* 🇫🇷 SEARCH: French URL */}
                            <Route path="/recherche" element={<Navigate to="/subventions/recherche" replace />} />
                            {/* 🔄 REDIRECT: English → French */}
                            <Route path="/search" element={<Navigate to="/subventions/recherche" replace />} />
                            
                            {/* PROFILE ROUTES */}
                            <Route path="/profile/new" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="New Profile">
                                    <NewProfilePage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            {/* 🇫🇷 PROFILE ROUTES - French URLs */}
                            {/* PERF FIX: Redirect directly instead of loading UnifiedProfile then redirecting */}
                            <Route path="/profil/:profileId" element={<ProfileRedirect />} />
                            <Route path="/profil/:profileId/:tab" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Profile Tab">
                                    <UnifiedProfile />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            <Route path="/profil/:profileId/:tab/:documentId" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Profile Document">
                                    <UnifiedProfile />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            <Route path="/profil/:profileId/edit" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Profile Edit">
                                    <ProfileEditPage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />

                            {/* Redirect old English profile routes to French */}
                            <Route path="/profile/:profileId" element={<ProfileRedirect />} />
                            <Route path="/profile/:profileId/:tab" element={<ProfileRedirect />} />
                            <Route path="/profile/:profileId/:tab/:documentId" element={<ProfileRedirect />} />
                            <Route path="/profile/:profileId/edit" element={<ProfileRedirect />} />

                            {/* SUBSIDY ROUTES - FRENCH URLs */}
                            {/* 🇫🇷 PRIMARY: French URLs */}
                            <Route path="/subventions" element={<Navigate to="/subventions/recherche" replace />} />
                            <Route path="/subventions/recherche" element={
                              <AnalyticsRouteWrapper pageName="Subsidy Search">
                                <SubsidySearchPage />
                              </AnalyticsRouteWrapper>
                            } />
                            <Route path="/subventions/:subsidyId" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Subsidy Detail">
                                    <SubsidyDetailPage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            <Route path="/subventions/:subsidyId/postuler" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Application Form">
                                    <ApplicationFormPage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />

                            {/* 🔄 REDIRECTS: English → French (preserve external links) */}
                            <Route path="/subsidies" element={<Navigate to="/subventions/recherche" replace />} />
                            <Route path="/subsidies/search" element={<Navigate to="/subventions/recherche" replace />} />
                            {/* Legacy redirect for subsidies with ID - uses SubsidyRedirect component to properly forward params */}
                            <Route path="/subsidies/:subsidyId" element={<SubsidyRedirect />} />
                            <Route path="/subsidies/:subsidyId/apply" element={<SubsidyRedirect />} />
                            <Route path="/subsidy/:subsidyId" element={<SubsidyRedirect />} />

                            {/* AI ASSISTANT */}
                            <Route path="/assistant" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="AI Assistant">
                                    <AIAssistantPage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            {/* Alternative paths */}
                            <Route path="/chatbot" element={<Navigate to="/assistant" replace />} />
                            <Route path="/assistant-ia" element={<Navigate to="/assistant" replace />} />

                            {/* CALENDAR ROUTE - FRENCH URL */}
                            {/* 🇫🇷 PRIMARY: French URL */}
                            <Route path="/calendrier" element={
                              <OnboardingGuardWrapper>
                                <TierAccessGate requireFullAccess>
                                  <AnalyticsRouteWrapper pageName="Calendar">
                                    <CalendarPage />
                                  </AnalyticsRouteWrapper>
                                </TierAccessGate>
                              </OnboardingGuardWrapper>
                            } />
                            {/* 🔄 REDIRECT: English → French */}
                            <Route path="/calendar" element={<Navigate to="/calendrier" replace />} />

                            {/* NEW ADMIN - Clean admin panel (based on masubventionpro) */}
                            <Route path="/admin" element={<AdminRoute />}>
                              <Route element={<AdminLayout />}>
                                <Route index element={<AdminDashboardPage />} />
                                <Route path="users" element={<AdminUsersPage />} />
                                <Route path="users/:id" element={<AdminUserDetailPage />} />
                                <Route path="subsidies" element={<AdminSubsidiesPage />} />
                                <Route path="subsidies/new" element={<AdminSubsidyEditPage />} />
                                <Route path="subsidies/:id" element={<AdminSubsidyEditPage />} />
                                <Route path="blog" element={<AdminBlogPage />} />
                                <Route path="blog/new" element={<AdminBlogEditPage />} />
                                <Route path="blog/:id" element={<AdminBlogEditPage />} />
                              </Route>
                            </Route>

                            {/* LEGACY ADMIN - Archived engineering admin (UnifiedAdmin) */}
                            <Route path="/admin-legacy/*" element={
                              <ProtectedRouteWrapper>
                                <AnalyticsRouteWrapper pageName="Admin Legacy">
                                  <UnifiedAdmin />
                                </AnalyticsRouteWrapper>
                              </ProtectedRouteWrapper>
                            } />

                            {/* HELP CENTER - Unified */}
                            <Route path="/help" element={
                              <AnalyticsRouteWrapper pageName="Help Center">
                                <UnifiedHelp />
                              </AnalyticsRouteWrapper>
                            } />
                            <Route path="/help/:category" element={
                              <AnalyticsRouteWrapper pageName="Help Category">
                                <UnifiedHelp />
                              </AnalyticsRouteWrapper>
                            } />
                            <Route path="/help/:category/:slug" element={
                              <AnalyticsRouteWrapper pageName="Help Article">
                                <UnifiedHelp />
                              </AnalyticsRouteWrapper>
                            } />

            {/* SETTINGS - FRENCH & ENGLISH URLs */}
            {/* 🇫🇷 PRIMARY: French URL */}
            <Route path="/parametres" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Settings">
                  <SettingsPage />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />
            {/* 🔄 REDIRECT: English → French for consistency */}
            <Route path="/settings" element={<Navigate to="/parametres" replace />} />

            {/* BULK IMPORT */}
            <Route path="/bulk-import" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Bulk Import">
                  <BulkImportPage />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />

            {/* PREFERENCES */}
            <Route path="/preferences/email" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Email Preferences">
                  <EmailPreferencesPage />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />

            {/* Unsubscribe - Public route (no auth required, token-validated) */}
            <Route path="/preferences/unsubscribe" element={
              <AnalyticsRouteWrapper pageName="Unsubscribe">
                <UnsubscribePage />
              </AnalyticsRouteWrapper>
            } />

            {/* ORGANIZATION ROUTES (Enterprise Tier) */}
            <Route path="/organization/setup" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Organization Setup">
                  <OrganizationSetup />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />
            <Route path="/organization/:orgSlug/dashboard" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Team Dashboard">
                  <TeamDashboard />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />
            <Route path="/organization/:orgSlug/*" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Team Dashboard">
                  <TeamDashboard />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />

            {/* MONITORING */}
            <Route path="/monitoring" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Monitoring">
                  <MonitoringPage />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />

            {/* URL HEALTH DASHBOARD */}
            <Route path="/url-health" element={
              <ProtectedRouteWrapper>
                <AnalyticsRouteWrapper pageName="URL Health">
                  <URLHealthDashboardPage />
                </AnalyticsRouteWrapper>
              </ProtectedRouteWrapper>
            } />

            {/* SEO DASHBOARD */}
            <Route path="/seo-dashboard" element={
              <ProtectedRouteWrapper>
                <AnalyticsRouteWrapper pageName="SEO Dashboard">
                  <SEODashboardPage />
                </AnalyticsRouteWrapper>
              </ProtectedRouteWrapper>
            } />

            {/* REGIONAL LANDING PAGES (SEO) */}
            <Route path="/regions/ile-de-france" element={
              <AnalyticsRouteWrapper pageName="Île-de-France Region">
                <IleDeFrancePage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/regions/auvergne-rhone-alpes" element={
              <AnalyticsRouteWrapper pageName="Auvergne-Rhône-Alpes Region">
                <AuvergneRhoneAlpesPage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/regions/nouvelle-aquitaine" element={
              <AnalyticsRouteWrapper pageName="Nouvelle-Aquitaine Region">
                <NouvelleAquitainePage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/regions/occitanie" element={
              <AnalyticsRouteWrapper pageName="Occitanie Region">
                <OccitaniePage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/regions/provence-alpes-cote-dazur" element={
              <AnalyticsRouteWrapper pageName="PACA Region">
                <PACAPage />
              </AnalyticsRouteWrapper>
            } />

            {/* EDUCATIONAL GUIDE PAGES (SEO) */}
            <Route path="/guides/subventions-france-2025" element={
              <AnalyticsRouteWrapper pageName="Guide Subventions France 2025">
                <GuideSubventionsFrance2025 />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/guides/bpi-france" element={
              <AnalyticsRouteWrapper pageName="Guide BPI France">
                <GuideBPIFrance />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/guides/subventions-vs-prets-vs-avances" element={
              <AnalyticsRouteWrapper pageName="Guide Subventions vs Prêts vs Avances">
                <GuideSubventionsVsPrets />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/guides/maprimerenov-2025" element={
              <AnalyticsRouteWrapper pageName="Guide MaPrimeRénov 2025">
                <GuideMaPrimeRenov />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/guides/agriculture-biologique-2025" element={
              <AnalyticsRouteWrapper pageName="Guide Agriculture Biologique 2025">
                <GuideAgricultureBio />
              </AnalyticsRouteWrapper>
            } />

            {/* BLOG PAGE (Public - LinkedIn posts) */}
            <Route path="/blog" element={
              <AnalyticsRouteWrapper pageName="Blog">
                <BlogPage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/blog/:slug" element={
              <AnalyticsRouteWrapper pageName="Blog Article">
                <BlogArticlePage />
              </AnalyticsRouteWrapper>
            } />

            {/* PRODUCT LANDING PAGES */}
            {/* HIDDEN: Simulateurs - redirected to landing */}
            <Route path="/simulateurs" element={<Navigate to="/" replace />} />
            <Route path="/plateforme" element={
              <AnalyticsRouteWrapper pageName="Plateforme Landing">
                <PlateformeLandingPage />
              </AnalyticsRouteWrapper>
            } />

            {/* AUDIENCE-SPECIFIC LANDING PAGES */}
            <Route path="/entreprises" element={
              <AnalyticsRouteWrapper pageName="Entreprises Landing">
                <EntreprisesPage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/consultants" element={
              <AnalyticsRouteWrapper pageName="Consultants Landing">
                <ConsultantsPage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/particuliers" element={
              <AnalyticsRouteWrapper pageName="Particuliers Landing">
                <ParticuliersPage />
              </AnalyticsRouteWrapper>
            } />

            {/* LEGAL PAGES */}
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
            <Route path="/cgv" element={<CGVPage />} />

            {/* BLOG PAGE (Public - LinkedIn posts) */}
            <Route path="/blog" element={
              <AnalyticsRouteWrapper pageName="Blog">
                <BlogPage />
              </AnalyticsRouteWrapper>
            } />
            <Route path="/blog/:slug" element={
              <AnalyticsRouteWrapper pageName="Blog Article">
                <BlogArticlePage />
              </AnalyticsRouteWrapper>
            } />

            <Route path="/privacy-dashboard" element={
              <OnboardingGuardWrapper>
                <AnalyticsRouteWrapper pageName="Privacy Dashboard">
                  <PrivacyDashboardPage />
                </AnalyticsRouteWrapper>
              </OnboardingGuardWrapper>
            } />

            {/* QA TEST ROUTE */}
            <Route path="/qa-test" element={
              <AnalyticsRouteWrapper pageName="QA Test">
                <QATestPage />
              </AnalyticsRouteWrapper>
            } />

            {/* PROFILE AWARE SYSTEM TEST - Removed in Phase 3 */}

                            {/* DEVELOPMENT ROUTES - CLEANED UP: Legacy test pages removed */}


                            {/* Legacy help routes redirect to unified help */}
                            <Route path="/french-subsidies" element={
                              <Navigate to="/help/french-subsidies" replace />
                            } />
                            <Route path="/supported-file-types" element={
                              <Navigate to="/help/file-types" replace />
                            } />
                            <Route path="/review-workflow" element={
                              <Navigate to="/help/workflow" replace />
                            } />

                             {/* Clean profile routes - no legacy support */}

                            {/* Legacy dashboard routes redirect to unified dashboard */}
                            <Route path="/employee-dashboard" element={
                              <Navigate to="/dashboard" replace />
                            } />
                            <Route path="/consultant-dashboard" element={
                              <Navigate to="/dashboard" replace />
                            } />
                            <Route path="/organization-dashboard" element={
                              <Navigate to="/dashboard" replace />
                            } />

                            {/* Legacy admin routes redirect to new domain structure */}
                            <Route path="/admin/dashboard" element={<Navigate to="/admin/overview" replace />} />
                            <Route path="/admin/subsidies" element={<Navigate to="/admin/data/subsidies" replace />} />
                            <Route path="/admin/system" element={<Navigate to="/admin/observability/health" replace />} />
                            <Route path="/admin/security" element={<Navigate to="/admin/security/monitor" replace />} />
                            <Route path="/admin/poc" element={<Navigate to="/admin/system/poc" replace />} />
                            <Route path="/admin/dev" element={<Navigate to="/admin/system/testing" replace />} />

                            {/* 404 */}
                            <Route path="*" element={<NotFound />} />
                            </Routes>
                            </main>
                          </SuspenseWrapper>
                        </LayoutWrapper>
                        <CookieConsentBanner />
                      </div>
                     </SubscriptionAccessWrapper>
                     </BetaWrapper>
                     </SecurityWrapper>
                      </NestedRouteProvider>
                  </CalendarProvider>
                </RoleProvider>
              </AdminProvider>
            </AuthGate>
          </OrganizationProvider>
          </SubscriptionProvider>
        </AuthProvider>
       </QueryClientProvider>
       </GlobalErrorBoundary>
     </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default React.memo(App);